/**
 * AI Recruit - Roles & Permissions Admin Page
 * Available under Organization Settings > Roles & Permissions (admin only).
 *
 * Dynamic RBAC admin UI per AI_RECRUIT_DYNAMIC_RBAC_MASTER_PLAN.md §9 — talks
 * to the /admin/roles + /admin/permissions API. As of the §10.1 frontend
 * rewrite (2026-08-14), the rest of the app's UI gating reads live grants
 * from GET /auth/me/capabilities too (via usePermission()) — the old static
 * config/permissions.ts ROLE_PERMISSIONS table is gone; that file now only
 * exports the Capability type.
 *
 * Create/edit moved from an in-page modal to a full page (2026-08-16, §22) —
 * this file now only lists roles and links to /admin/roles/new or
 * /admin/roles/:roleId; see RoleDetailPage.tsx for the actual form.
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  ShieldCheckIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'

import { rolesApi } from '@/services/api'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { usePermission } from '@/hooks/usePermission'

// ─── Types ──────────────────────────────────────────────────────────────────

interface RoleSummary {
  id: string
  name: string
  legacy_key: string | null
  description: string | null
  sort_order: number
  is_system: boolean
  is_bypass_all: boolean
  user_count: number
  created_at: string
  updated_at: string
}

// ─── Confirm Delete Modal ────────────────────────────────────────────────────

const ConfirmModal: React.FC<{
  title: string
  message: string
  onConfirm: (reason?: string) => void
  onCancel: () => void
  loading?: boolean
  // Deletion is destructive enough to warrant an optional audit-trail
  // note — shown here, not on every confirm dialog in the app.
  showReasonInput?: boolean
}> = ({ title, message, onConfirm, onCancel, loading, showReasonInput }) => {
  const [reason, setReason] = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 mx-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <TrashIcon className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        {showReasonInput && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Shown in the activity log alongside this deletion"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="btn-outline text-sm px-4 py-2 rounded-lg">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(showReasonInput ? (reason.trim() || undefined) : undefined)}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {loading && <LoadingSpinner size="sm" variant="white" />}
            {loading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Role Row ────────────────────────────────────────────────────────────────

const RoleRow: React.FC<{
  role: RoleSummary
  onEdit: (role: RoleSummary) => void
  onDelete: (role: RoleSummary) => void
}> = ({ role, onEdit, onDelete }) => {
  const { can } = usePermission()
  // Hidden, not disabled — per the hide-vs-disable convention this project's
  // been following throughout: a role you can never delete doesn't need a
  // visible-but-inert control (§9). roles.delete added 2026-08-31 (roles.manage
  // retirement) — this list page itself now only requires roles.viewList, so
  // the delete action needs its own explicit check where it never needed one
  // before (the old route-level gate covered it implicitly).
  const canDelete = can('roles.delete') && !role.is_system && !role.is_bypass_all && role.user_count === 0

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-5 py-4">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-gray-900">{role.name}</p>
          {role.is_bypass_all && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
              <ShieldCheckIcon className="w-3 h-3" /> Full Access
            </span>
          )}
          {role.is_system && !role.is_bypass_all && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
              system
            </span>
          )}
        </div>
        {role.description && <p className="text-xs text-gray-500 mt-0.5">{role.description}</p>}
      </td>
      <td className="px-5 py-4">
        <span className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
          {role.user_count}
        </span>
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(role)}
            title="Edit role"
            className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          {canDelete && (
            <button
              onClick={() => onDelete(role)}
              title="Delete role"
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const RolesPermissionsPage: React.FC = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { can } = usePermission()

  const [deleteTarget, setDeleteTarget] = useState<RoleSummary | null>(null)

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => rolesApi.list().then((r) => r.data as { roles: RoleSummary[]; total: number }),
  })

  const roles: RoleSummary[] = rolesData?.roles ?? []

  const deleteMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => rolesApi.delete(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-roles'] })
      toast.success('Role deleted')
      setDeleteTarget(null)
    },
    // The global apiV2 response interceptor (services/api.ts) already toasts
    // every 403 app-wide — toasting the same `detail` here too double-fires
    // (same root cause as the Edit Role page's stacked-toast bug).
    onError: (e: any) => {
      if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail ?? 'Failed to delete role')
    },
  })

  if (rolesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
            Roles &amp; Permissions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Create custom roles and control exactly what each one can do.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {can('roles.viewAuditLog') && (
            <button
              onClick={() => navigate('/admin/audit-log')}
              className="btn-outline flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg"
            >
              <ClockIcon className="w-4 h-4" />
              Activity Log
            </button>
          )}
          {can('roles.create') && (
            <button
              onClick={() => navigate('/admin/roles/new')}
              className="btn-primary flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg"
            >
              <PlusIcon className="w-4 h-4" />
              Add New Role
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Scrolls internally past a tall role list instead of pushing the
            page itself into a long scroll — header row stays put (sticky)
            so column labels stay visible while scrolling. */}
        <div className="overflow-y-auto max-h-[65vh]">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="bg-gray-50">
                {['Role', 'Users', 'Actions'].map((h) => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {roles.map((role) => (
                <RoleRow
                  key={role.id}
                  role={role}
                  onEdit={(r) => navigate(`/admin/roles/${r.id}`)}
                  onDelete={setDeleteTarget}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Delete Role"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          loading={deleteMut.isPending}
          showReasonInput
          onConfirm={(reason) => deleteMut.mutate({ id: deleteTarget.id, reason })}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}

export default RolesPermissionsPage
